import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {Button, Space} from 'antd';
import {PlusOutlined} from '@ant-design/icons';
import {StyledBudgets, StyledBudgetsHeader} from './StyledBudgets';
import {amountHelpers, tabHelpers, tableHelpers} from '../../../../../utils/helpers';
import {useTabState} from '../../../../../hooks';
import EditButton from '../../../../EditButton';
import EmptyOrTable from '../../../../EmptyOrTable';
import UserDetails from '../../../../UserDetails';
import CostCenter from '../../../SubscriptionsPage/CostCenter';
import LoadMoreButton from '../../../TransactionsPage/LoadMoreButton';
import AddBudgetModal from '../../tabComponents/AddBudgetModal';
import Policies from '../Policies';
import {budgetActions} from '../../../../../state/actions/budget.actions';
import {DEFAULT_MODAL_PROPS} from '../../../../../constants';

const columnProps = { width: 'calc(100% / 4)' };

const view = 'budgets';

const Budgets = ({
  ...rest
}) => {
  const [t] = useTranslation('main');
  const dispatch = useDispatch();
  const [addModalProps, setAddModalProps] = useState({ ...DEFAULT_MODAL_PROPS, mode: 'add', budget: null });

  const {
    tableData, setTableData,
    queries, setQueries,
    loading, setLoading,
  } = useTabState({tabKeys: [view]});

  useEffect(() => {
    loadData({ initial: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const finishTableLoading = () => {
    setLoading(prevState => ({...prevState, [view]: false}));
  }

  const loadData = ({
    query = null,
    initial = false,
  }) => {
    const viewQuery = tabHelpers.getViewQuery({
      initial, query, queries, view, tableData
    });

    setQueries({ ...queries, [view]: viewQuery});

    setLoading(prevState => ({...prevState, [view]: true}));

    dispatch(
      budgetActions.getBudgets({
        query: viewQuery,
        successCallback: (response) => {
          tabHelpers.handleStoreTableDataWithPrev({
            tableData,
            setTableData,
            view,
            response,
            initial,
          });

          finishTableLoading();
        },
        errorCallback: finishTableLoading
      })
    );
  }

  const handleOnTableChange = (pagination, filters, sorter, extra) => {
    tabHelpers.handleOnTableChange({view, sorter, extra, loadDataFunc: loadData});
  }

  const columns = [
    {
      ...columnProps,
      dataIndex: 'name',
      title: t('name'),
    },
    {
      ...columnProps,
      dataIndex: 'reported_by',
      title: t('Owner'),
      render: (user) => user && <UserDetails email={false} user={user} avatarProps={{size: 24}} />
    },
    {
      ...columnProps,
      dataIndex: 'cost_centers',
      title: t('costCenter'),
      render: (tags) => <CostCenter tags={tags} />
    },
    {
      ...columnProps,
      dataIndex: 'limit_amount',
      title: `${t('Budget')} ${t('limit')}`,
      render: (value) => amountHelpers.getAmountWithCurrencyCode(value)
    },
    {
      className: 'actions-column-cell',
      dataIndex: 'actions',
      title: ' ',
      width: 30,
      render: (_, record) => <EditButton onClick={() => handleOpenEditModal(record)} />
    }

  ];

  const handleOpenCreateModal = () => {
    setAddModalProps({ ...DEFAULT_MODAL_PROPS, mode: 'add', open: true });
  }

  const handleOpenEditModal = (budget) => {
    setAddModalProps({ ...DEFAULT_MODAL_PROPS, mode: 'edit', open: true, budget });
  }

  const handleCloseAddModal = () => {
    setAddModalProps(prevState => ({...prevState, loading: false, open: false}));
  }

  const handleOkAddModal = (data, successCallback, errorCallback) => {
    setAddModalProps(prevState => ({...prevState, loading: true}));

    const isAddMode = addModalProps.mode === 'add';

    const action = isAddMode
      ? budgetActions.createBudget
      : budgetActions.updateBudget;

    dispatch(
      action({
        id: addModalProps.budget?.id,
        data,
        successCallback: (response) => {
          const addToStoreAction = isAddMode
            ? budgetActions.addBudgetToStore
            : budgetActions.updateBudgetInStore;

          dispatch(addToStoreAction(response));

          handleCloseAddModal();

          if (addModalProps.mode === 'edit') {
            // update table data row
            setTableData(prevState => ({
              ...prevState,
              [view]: {
                ...prevState?.[view],
                rows: tableData[view]?.rows?.map(budget => (
                  budget.id === response.id ? response : budget
                ))
              }
            }));
            successCallback?.(response);
            return;
          }
          dispatch(budgetActions.addBudgetToStore(response));

          loadData({ initial: true });
          successCallback?.(response);
        },
        errorCallback: (error) => {
          setAddModalProps(prevState => ({...prevState, loading: false}));
          errorCallback?.(error);
        }
      })
    );
  }

  const tableProps = {
    onChange: (pagination, filters, sorter, extra) => handleOnTableChange(pagination, filters, sorter, extra),
    onRow: (record) => ({
      className: 'has-actions-column',
      onClick: () => handleOpenEditModal(record),
    }),
    pagination: false,
  }

  const loadMoreProps = tableHelpers.getLoadMoreProps({tableData, view, loadData});

  return (
    <StyledBudgets direction='vertical' size='large' {...rest}>
      <Space size='small' direction='vertical' className='w-100'>
        <StyledBudgetsHeader>
          <h3>{t('Budget')}</h3>
          <Button
            icon={<PlusOutlined />}
            onClick={handleOpenCreateModal}
            type='primary'
            size='small'
          >
            {t('Budget')}
          </Button>
        </StyledBudgetsHeader>
        <EmptyOrTable
          data={tableData?.[view]?.rows}
          loading={loading?.[view]}
          query={queries?.[view]}
          columns={columns}
          tableProps={tableProps}
        />

        <LoadMoreButton
          {...loadMoreProps}
          loading={loading?.[view] || loadMoreProps.loading}
          size='small'
        >
          {t('showMore')}
        </LoadMoreButton>
      </Space>

      <Policies className='budgets-policies' policies={[{key: 'procurement_policy'}]} />

      <AddBudgetModal
        {...addModalProps}
        handleCancel={handleCloseAddModal}
        handleOk={handleOkAddModal}
      />

    </StyledBudgets>
  );
}

export default Budgets;
